import React, { useState } from 'react';
import { Card, CardContent, Typography, TextField, IconButton, Box, Collapse } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';

import type { OverviewItem } from '@/shared/types/overview';
import type { UUID, StoredStandard } from '@/shared/types/customStandard';
import { AUTHORED_CONTENT_CLASS, authoredContentSx } from '@/shared/theme/authored-content';
import { useConfirm } from '@/shared/components/ConfirmProvider';
import StandardsSelector from '@/shared/components/StandardsSelector';
import LessonBlockEditor from '@/features/lessons/components/LessonBlockEditor';

interface EditableOverviewItemProps {
  item: OverviewItem;
  index: number;
  onUpdate: (updatedItem: OverviewItem) => Promise<void>;
  onDelete: (index: number) => Promise<void>;
  canEdit: boolean;
  isUpdating?: boolean;
  // For standards-type sections
  ownerId?: UUID;
  parentScope?: StoredStandard[];
  allowAllToggle?: boolean;
  scopeLabel?: string;
}

export const EditableOverviewItem: React.FC<EditableOverviewItemProps> = ({
  item,
  index,
  onUpdate,
  onDelete,
  canEdit,
  isUpdating = false,
  ownerId,
  parentScope,
  allowAllToggle,
  scopeLabel,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedCategoryName, setEditedCategoryName] = useState(item.categoryName);
  const confirm = useConfirm();
  const isMobile = false; // TODO: wire to UI state if needed

  const handleEdit = () => {
    setIsEditing(true);
    setEditedCategoryName(item.categoryName);
  };

  const handleSaveHtml = async (html: string) => {
    if (item.type !== 'text') return;

    try {
      await onUpdate({ ...item, categoryName: editedCategoryName, html });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save changes:', error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedCategoryName(item.categoryName);
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete',
      message: `Are you sure you want to delete? This action cannot be undone.`,
      confirmButtonText: 'Delete',
    });

    if (confirmed) {
      try {
        await onDelete(index);
      } catch (error) {
        console.error('Failed to delete item:', error);
      }
    }
  };

  const isStandards = item.type === 'standards';

  return (
    <Card sx={{ mb: 1 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flexGrow: 1 }}>
            {item.type === 'standards' ? (
              /* ── Standards item: selector is always visible, edit toggles category name ── */
              <>
                <Collapse in={!isEditing}>
                  <Typography variant="h4" gutterBottom>
                    {item.categoryName}
                  </Typography>
                </Collapse>
                <Collapse in={isEditing}>
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      label="Category Name"
                      value={editedCategoryName}
                      onChange={(e) => setEditedCategoryName(e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                      <IconButton size="small" onClick={handleCancel} disabled={isUpdating}>
                        <CancelIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={isUpdating}
                        onClick={async () => {
                          if (item.type !== 'standards') return;

                          try {
                            await onUpdate({ ...item, categoryName: editedCategoryName });
                            setIsEditing(false);
                          } catch (error) {
                            console.error('Failed to save category name:', error);
                          }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Collapse>
                <Box sx={{ mt: 1 }}>
                  <StandardsSelector
                    ownerId={ownerId}
                    value={item.standards ?? []}
                    onChange={(newStandards) => {
                      if (item.type !== 'standards') return;
                      void onUpdate({ ...item, standards: newStandards }).catch((error) => {
                        console.error('Failed to save standards:', error);
                      });
                    }}
                    label={item.categoryName || 'Standards'}
                    disabled={!canEdit}
                    parentScope={parentScope}
                    allowAllToggle={allowAllToggle}
                    scopeLabel={scopeLabel}
                  />
                </Box>
              </>
            ) : (
              /* ── Text item: full Tiptap edit mode ── */
              <>
                {/* View mode */}
                <Collapse in={!isEditing}>
                  <Typography variant="h4" gutterBottom>
                    {item.categoryName}
                  </Typography>
                  {item.html ? (
                    <Box
                      sx={{
                        cursor: canEdit && !isMobile ? 'pointer' : 'default',
                        [`& .${AUTHORED_CONTENT_CLASS}`]: authoredContentSx,
                      }}
                      onClick={canEdit && !isMobile ? handleEdit : undefined}
                    >
                      <div
                        className={AUTHORED_CONTENT_CLASS}
                        dangerouslySetInnerHTML={{ __html: item.html }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        p: 0,
                        textAlign: 'center',
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        cursor: canEdit && !isMobile ? 'pointer' : 'default',
                      }}
                      onClick={canEdit && !isMobile ? handleEdit : undefined}
                    >
                      {canEdit && !isMobile ? 'Click to add content...' : 'No content available'}
                    </Box>
                  )}
                </Collapse>

                {/* Edit mode — Tiptap rich-text editor */}
                <Collapse in={isEditing}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Category Name"
                      value={editedCategoryName}
                      onChange={(e) => setEditedCategoryName(e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                    <LessonBlockEditor
                      initialHtml={item.html || ''}
                      onSave={handleSaveHtml}
                      onCancel={handleCancel}
                    />
                  </Box>
                </Collapse>
              </>
            )}
          </Box>
          {canEdit && !isMobile && (
            <Box sx={{ ml: 2, display: 'flex', gap: 1 }}>
              {!isEditing && (
                <>
                  {isStandards && (
                    <IconButton size="small" onClick={handleEdit} disabled={isUpdating}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  {!isStandards && (
                    <IconButton size="small" onClick={handleEdit} disabled={isUpdating}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={handleDelete}
                    disabled={isUpdating}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
